import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import ToolPrepPopup from "../../../shared/Popup/ToolPrepPopup";
import TransformPreview from "./TransformPreview";
import TransformPresetCards from "./TransformPresetCards";
import RotatePrepCard from "../../rotate/Popup/RotatePrepCard";
import Flip from "../../flip/Editor/Flip";
import ImagePickerModal from "../../../../../components/popups/imagePicker/ImagePickerModal";
import { useToolPrepImageSource } from "../../../hooks/useToolPrepImageSource";
import { useSyncScrollbarWidth } from "../../../hooks/useSyncScrollbarWidth";
import { useEditorStore } from "../../../../../state/editorStore";
import { ensureBackgroundRemoved } from "../../../../../utils/ensureBackgroundRemoved";
import {
  TOOL_PREP_FOOTER_HINT,
  TOOL_PREP_PREVIEW_BG,
  TOOL_PREP_PREPARING_PREVIEW_LABEL,
  initialToolPrepPreviewBg,
} from "../../../../../utils/editorEntrySession";
import { useToolPrepImageDownload } from "../../../hooks/useToolPrepImageDownload";
import ToolPrepDownloadFlowModals from "../../../shared/Popup/ToolPrepDownloadFlowModals";
import "./TransformToolPrep.css";

/**
 * Always 2 cards: Original | Rotate/Flip.
 * Remove BG off → action card uses uploaded image.
 * Remove BG on  → action card + stage use the white Preview cutout.
 */
const TransformToolPrep = ({
  mode = "rotate",
  originalImageUrl = null,
  imageFile = null,
  removeBgDefault = false,
  cachedCutoutUrl = null,
  onCutoutReady = null,
  onDone = () => {},
  onCancel = () => {},
}) => {
  const { getToken } = useAuth();
  const updateObject = useEditorStore((s) => s.updateObject);
  const selectLayer = useEditorStore((s) => s.selectLayer);
  const rotation = useEditorStore((s) => Number(s.object?.rotation) || 0);
  const flipH = useEditorStore((s) => Boolean(s.object?.flipH));
  const flipV = useEditorStore((s) => Boolean(s.object?.flipV));

  const actionKey = mode === "flip" ? "flip" : "rotate";
  const [selectedKey, setSelectedKey] = useState("original");
  const [removeBg, setRemoveBg] = useState(Boolean(removeBgDefault));
  const [cutoutUrl, setCutoutUrl] = useState(cachedCutoutUrl || null);
  const [creationId, setCreationId] = useState(null);
  const [bgLoading, setBgLoading] = useState(false);
  const [bgError, setBgError] = useState(null);
  const [doneBusy, setDoneBusy] = useState(false);
  const bgRequestRef = useRef(null);
  const seededRef = useRef(false);
  const scrollBodyRef = useRef(null);

  const {
    sourceImageUrl,
    resolvedFile,
    sourceFileName,
    sourceFileSizeLabel,
    clearSource,
    openUploadPicker,
    imagePickerProps,
    uploadProgress,
  } = useToolPrepImageSource({
    originalImageUrl,
    imageFile,
    busy: bgLoading || doneBusy,
    onSourceChange: () => {
      setCutoutUrl(null);
      setCreationId(null);
      setRemoveBg(false);
      setBgError(null);
      setSelectedKey("original");
    },
  });

  useEffect(() => {
    selectLayer("object");
  }, [selectLayer]);

  useEffect(() => {
    if (seededRef.current) return;
    seededRef.current = true;
    updateObject("rotation", 0, { recordHistory: false });
    updateObject("flipH", false, { recordHistory: false });
    updateObject("flipV", false, { recordHistory: false });
  }, [updateObject]);

  useEffect(() => {
    if (cachedCutoutUrl) setCutoutUrl(cachedCutoutUrl);
  }, [cachedCutoutUrl]);

  // When controls change the transform, prefer the action card — but never
  // clear transform state when the user picks "Original", and never force
  // Original just because the angle is 0° / 360°.
  const prevTransformRef = useRef({
    rotation: 0,
    flipH: false,
    flipV: false,
  });
  useEffect(() => {
    const prev = prevTransformRef.current;
    const rot = Number(rotation) || 0;

    if (mode === "rotate") {
      if (rot !== (Number(prev.rotation) || 0)) {
        setSelectedKey("rotate");
      }
    } else {
      const identity = !flipH && !flipV;
      const prevIdentity = !prev.flipH && !prev.flipV;
      if (!prevIdentity && identity) {
        // Flip reset → stay on Flip card (state is cleared); user can pick Original.
        setSelectedKey("flip");
      } else if (flipH !== prev.flipH || flipV !== prev.flipV) {
        setSelectedKey("flip");
      }
    }

    prevTransformRef.current = {
      rotation: rot,
      flipH: Boolean(flipH),
      flipV: Boolean(flipV),
    };
  }, [rotation, flipH, flipV, mode]);

  const runBackgroundRemoval = useCallback(async () => {
    if (cutoutUrl?.startsWith("blob:") || cutoutUrl?.startsWith("data:")) {
      return { cutoutUrl, creationId };
    }

    const requestId = Symbol("bg");
    bgRequestRef.current = requestId;
    setBgLoading(true);
    setBgError(null);

    try {
      const token = await getToken();
      const { cutoutUrl: nextUrl, creationId: cid } =
        await ensureBackgroundRemoved({
          token,
          imageFile: resolvedFile,
          imageUrl: sourceImageUrl,
          cachedCutoutUrl: cutoutUrl || cachedCutoutUrl,
        });
      if (bgRequestRef.current !== requestId) return null;
      setCutoutUrl(nextUrl);
      if (cid != null) setCreationId(cid);
      onCutoutReady?.(nextUrl);
      // Stay on action card; do not invent a default flip/rotate.
      setSelectedKey(actionKey);
      return { cutoutUrl: nextUrl, creationId: cid ?? creationId };
    } catch (err) {
      if (bgRequestRef.current === requestId) {
        setBgError(err?.message || "Failed to remove background");
      }
      throw err;
    } finally {
      if (bgRequestRef.current === requestId) {
        setBgLoading(false);
      }
    }
  }, [
    actionKey,
    cachedCutoutUrl,
    creationId,
    cutoutUrl,
    getToken,
    mode,
    onCutoutReady,
    resolvedFile,
    sourceImageUrl,
    updateObject,
  ]);

  const handleRemoveBgToggle = async () => {
    if (!sourceImageUrl || bgLoading) return;
    const next = !removeBg;
    setRemoveBg(next);
    if (!next) {
      setBgError(null);
      return;
    }
    try {
      await runBackgroundRemoval();
    } catch {
      setRemoveBg(false);
    }
  };

  const handlePresetSelect = (key) => {
    // Original / Rotate|Flip cards only switch the stage preview.
    // Transform state lives on the action card and is not cleared here.
    setSelectedKey(key);
  };

  const onActionCard = selectedKey === actionKey;
  const useCutoutStage = Boolean(removeBg && cutoutUrl && onActionCard);

  const stageImageUrl = useCutoutStage ? cutoutUrl : sourceImageUrl;
  const resultPreviewBg = removeBg
    ? initialToolPrepPreviewBg()
    : TOOL_PREP_PREVIEW_BG.ORIGINAL;
  const stagePreviewBg = useCutoutStage
    ? resultPreviewBg
    : TOOL_PREP_PREVIEW_BG.TRANSPARENT;
  const stageRotation = selectedKey === "original" ? 0 : rotation;
  const stageFlipH = selectedKey === "original" ? false : flipH;
  const stageFlipV = selectedKey === "original" ? false : flipV;

  const { downloadBusy, onDownload, downloadFlow } = useToolPrepImageDownload(
    stageImageUrl,
    sourceFileName,
    {
      enabled: Boolean(stageImageUrl),
      fallbackName: "transform.png",
      variant: "editing",
      toolKey: mode === "flip" ? "flip" : "rotate",
      shareTitle: mode === "flip" ? "Flip tool" : "Rotate tool",
    }
  );

  const handleDone = async () => {
    setDoneBusy(true);
    try {
      let imageUrl = sourceImageUrl;
      let doneCreationId = creationId;
      if (removeBg) {
        if (cutoutUrl) {
          imageUrl = cutoutUrl;
        } else {
          const removed = await runBackgroundRemoval();
          imageUrl = removed?.cutoutUrl;
          doneCreationId = removed?.creationId ?? doneCreationId;
        }
      }
      if (!imageUrl) {
        throw new Error("No image ready");
      }
      if (
        typeof imageUrl === "string" &&
        /^https?:\/\//i.test(imageUrl) &&
        !imageUrl.startsWith("blob:")
      ) {
        throw new Error(
          "Background-removed image could not be loaded for the editor (CORS). Please try Remove Background again."
        );
      }
      const obj = useEditorStore.getState().object || {};
      onDone({
        mode,
        rotation: Number(obj.rotation) || 0,
        flipH: Boolean(obj.flipH),
        flipV: Boolean(obj.flipV),
        removeBg,
        previewBg: resultPreviewBg,
        imageUrl,
        creationId: removeBg ? doneCreationId : null,
      });
    } catch (err) {
      setBgError(err?.message || "Could not finish setup");
    } finally {
      setDoneBusy(false);
    }
  };

  const title = mode === "flip" ? "Flip" : "Rotate";

  useSyncScrollbarWidth(scrollBodyRef, [mode, sourceImageUrl, removeBg]);

  const leftContent = (
    <div className="ttp-panel">
      <div className="ttp-sticky-top">
        <div className="ttp-preview-block">
          <TransformPresetCards
            mode={mode}
            rotation={rotation}
            flipH={flipH}
            flipV={flipV}
            selectedKey={selectedKey}
            onSelect={handlePresetSelect}
            originalImageUrl={sourceImageUrl}
            cutoutUrl={cutoutUrl}
            removeBgActive={removeBg}
            cutoutPending={bgLoading && !cutoutUrl}
            onUploadClick={openUploadPicker}
            onRemove={clearSource}
            uploadProgress={uploadProgress}
            sourceName={sourceFileName}
            sourceSizeLabel={sourceFileSizeLabel}
            actionsDisabled={bgLoading || doneBusy}
            removeBgControls={{
              active: removeBg,
              onToggle: handleRemoveBgToggle,
              disabled: bgLoading || !sourceImageUrl,
              bio:
                mode === "flip"
                  ? "Optional — flip the cutout subject. When off, flip applies to your original photo."
                  : "Optional — rotate the cutout subject. When off, rotate applies to your original photo.",
              error: bgError,
            }}
          />
        </div>
      </div>

      <div
        className="ttp-object-controls tool-prep-scroll"
        ref={scrollBodyRef}
      >
        {mode === "rotate" ? <RotatePrepCard forceExpanded /> : <Flip />}
      </div>
    </div>
  );

  return (
    <>
      <ImagePickerModal {...imagePickerProps} />
      <ToolPrepPopup
        toolKey={mode}
        title={title}
        left={leftContent}
        preview={
          <TransformPreview
            imageUrl={stageImageUrl}
            rotation={stageRotation}
            flipH={stageFlipH}
            flipV={stageFlipV}
            loading={bgLoading}
            previewBg={stagePreviewBg}
            showSubjectBox={Boolean(removeBg && cutoutUrl)}
            softWatermarkToolKey={mode === "flip" ? "flip" : "rotate"}
            emptyLabel={
              uploadProgress != null && !stageImageUrl
                ? TOOL_PREP_PREPARING_PREVIEW_LABEL
                : undefined
            }
          />
        }
        onDone={handleDone}
        onCancel={onCancel}
        confirmClose={Boolean(sourceImageUrl)}
        onDownload={onDownload}
        busy={doneBusy || bgLoading || downloadBusy}
        doneDisabled={!sourceImageUrl}
        doneLabel="Open in Editor"
        footerHint={TOOL_PREP_FOOTER_HINT}
      />
      <ToolPrepDownloadFlowModals
        flow={downloadFlow}
        toolKey={mode === "flip" ? "flip" : "rotate"}
        onPrimaryAction={() => {
          downloadFlow.handleThankYouDismiss();
        }}
        onBackToDashboard={onCancel}
      />
    </>
  );
};

export default TransformToolPrep;
